import React, { memo, useEffect, useMemo, useCallback, useState, useRef, Fragment } from 'react';
import sanitizeHtml from 'sanitize-html';
import { useIsHorizontalResizeActive } from '@Sage/ssm-web5';
import TextWithLinks from 'components/TextWithLinks';

const EmailIframe = memo(({ id, html }) => {
    const isIframeAttached = useRef(false);
    const iframeContainerRef = useRef(null);
    const [iframeHeight, setIframeHeight] = useState(0);
    const isHorizontalResizeActive = useIsHorizontalResizeActive();
    const isHorizontalResizeActiveRef = useRef(isHorizontalResizeActive);

    useEffect(() => {
        isHorizontalResizeActiveRef.current = isHorizontalResizeActive;
    }, [isHorizontalResizeActive]);

    const isHtml = useMemo(() => {
        let finalHtml = html;
        const doctypeMatch = finalHtml.match(/<!DOCTYPE[^>[]*(\[[^]]*\])?>/gm);
        if (doctypeMatch) {
            finalHtml = finalHtml.replace(/<!DOCTYPE[^>[]*(\[[^]]*\])?>/, '');
        }
        return /^<html[^>]*>[\s\S]*<\/html>/gi.test(finalHtml);
    }, [html]);

    const sanitize = useCallback((html) => {
        return sanitizeHtml(html, {
            allowedTags: sanitizeHtml.defaults.allowedTags.concat([
                'html',
                'head',
                'body',
                'img',
                'button',
            ]),
            allowedAttributes: {
                '*': [
                    'href',
                    'name',
                    'target',
                    'src',
                    'srcset',
                    'alt',
                    'title',
                    'width',
                    'height',
                    'loading',
                    'style',
                    'align',
                ],
            },
            allowedSchemes: ['data', 'http', 'https', 'cid'],
            enforceHtmlBoundary: false,
        });
    }, []);

    const correctFontFamilyQuotes = useCallback((html) => {
        if (!html) return '';
        let fontFamilyMatches = html.match(/font-family:.+?(?=;)/gm);
        const uniqFontFamilyMatches = [...new Set(fontFamilyMatches)];
        let newHtml = html;
        uniqFontFamilyMatches.forEach((current) => {
            // sorry :(
            if (current.includes('>')) {
                return;
            }
            let newCurrent = '';
            newCurrent = current.replace(/\"/g, "'");
            // we add also sans-serif as a backup font
            if (!newCurrent.includes('sans-serif')) newCurrent = `${newCurrent}, sans-serif`;
            newHtml = newHtml.replace(new RegExp(`${current}`, 'g'), `${newCurrent}`);
        });
        return newHtml;
    }, []);

    const setBodyFontFamily = useCallback((html) => {
        let finalHtml = html;
        let bodyMatch = html.match(/<body.+?(?=>)/gm);
        if (!bodyMatch?.[0]) {
            finalHtml = finalHtml.replace('<body>', '<body style="font-family: sans-serif">');
            return finalHtml;
        }
        let finalBodyTag = bodyMatch[0];

        let styleMatch = bodyMatch[0].match(/style=".+?(?=")/gm);
        if (!styleMatch?.[0]) {
            finalHtml = finalHtml.replace(
                bodyMatch,
                `${bodyMatch} style="font-family: sans-serif"`,
            );
            return finalHtml;
        }

        let fontFamilyMatches = styleMatch[0].match(/font-family:.+?(?=;)/gm);
        const fontFamily = fontFamilyMatches?.[0] || '';

        if (fontFamily && !fontFamily.includes('sans-serif')) {
            finalBodyTag = finalBodyTag.replace(fontFamily, `${fontFamily}, sans-serif`);
        } else if (!fontFamily) {
            finalBodyTag = finalBodyTag.slice(0, -1);
            const hasLastSemicolon = finalBodyTag.charAt(finalBodyTag.length - 1) === ';';
            if (hasLastSemicolon) finalBodyTag = `${finalBodyTag} font-family: sans-serif;"`;
            else finalBodyTag = `${finalBodyTag}; font-family: sans-serif;"`;
        }

        finalHtml = html.replace(bodyMatch[0], finalBodyTag);
        return finalHtml;
    }, []);

    const setTargetForLinks = useCallback((html) => {
        let finalHtml = html;
        let linkMatch = html.match(/<a.+?(?=>)/gm);
        const targetRegEx = /target=".+?"/g;
        const emptyTargetRegEx = /target=""/g;

        finalHtml = html.replace(targetRegEx, 'target="_blank"');
        finalHtml = finalHtml.replace(emptyTargetRegEx, 'target="_blank"');
        if (!linkMatch?.length) return finalHtml;

        linkMatch.forEach((current) => {
            if (!linkMatch.includes('target="_blank"')) {
                let finalLinkMatch = `${current} target="_blank"`;
                finalHtml = finalHtml.replace(current, finalLinkMatch);
            }
        });

        return finalHtml;
    }, []);

    const normalizeTableLayout = useCallback((html) => {
        if (!html || typeof DOMParser === 'undefined') return html;

        try {
            const parser = new DOMParser();
            const documentNode = parser.parseFromString(html, 'text/html');

            documentNode.querySelectorAll('table').forEach((table) => {
                table.style.removeProperty('width');
                table.style.removeProperty('min-width');
                table.style.maxWidth = '100%';
                table.style.width = '100%';
            });

            documentNode.querySelectorAll('td, th').forEach((cell) => {
                cell.style.removeProperty('width');
                cell.style.removeProperty('min-width');
                cell.style.maxWidth = '100%';
            });

            documentNode.querySelectorAll('table *').forEach((element) => {
                const textContent = element.textContent?.trim() || '';
                const hasLongText = textContent.length > 30;
                const hasLongWord = /[^\s]{20,}/.test(textContent);

                if (element.style?.whiteSpace === 'nowrap' && (hasLongText || hasLongWord)) {
                    element.style.whiteSpace = 'normal';
                    element.style.overflowWrap = 'anywhere';
                    element.style.wordBreak = 'break-word';
                }
            });

            documentNode.querySelectorAll('td, th').forEach((cell) => {
                const textContent = cell.textContent?.trim() || '';
                const isNumericLike = /^[\d\s.,€$£%+-]+$/.test(textContent);
                const isShortToken = textContent.length > 0 && textContent.length <= 20;

                if (isNumericLike && isShortToken) {
                    cell.style.whiteSpace = 'nowrap';
                    cell.style.overflowWrap = 'normal';
                    cell.style.wordBreak = 'normal';

                    const firstChild = cell.firstElementChild;
                    if (firstChild?.style) {
                        firstChild.style.whiteSpace = 'nowrap';
                        firstChild.style.overflowWrap = 'normal';
                        firstChild.style.wordBreak = 'normal';
                    }
                }
            });

            documentNode.querySelectorAll('img').forEach((img) => {
                img.style.maxWidth = '100%';
                img.style.height = 'auto';
            });

            return documentNode.documentElement.outerHTML;
        } catch (error) {
            console.error(error);
            return html;
        }
    }, []);

    const cleanHtml = useMemo(() => {
        let finalHtml = html.replace(/target="_self"/g, 'target="_blank"');

        // Remove all script and style tags even when they are declared between CSS to prevent XSS
        finalHtml = finalHtml.replace(new RegExp(/<script>([\s\S]*?)<\/script>/gim), '');
        finalHtml = finalHtml.replace(new RegExp(/<style>([\s\S]*?)<\/style>/gim), '');
        // fixing possible quote inconsistencies in the incoming html string
        try {
            finalHtml = correctFontFamilyQuotes(finalHtml);
        } catch (e) {
            console.error(e);
        }

        // set a default font-family
        finalHtml = setBodyFontFamily(finalHtml);
        // make all links open as _blank
        finalHtml = setTargetForLinks(finalHtml);
        // avoid large email table cells forcing overflow in the page layout
        finalHtml = normalizeTableLayout(finalHtml);
        finalHtml = sanitize(finalHtml);

        return finalHtml;
    }, [
        html,
        correctFontFamilyQuotes,
        setBodyFontFamily,
        setTargetForLinks,
        normalizeTableLayout,
        sanitize,
    ]);

    const containerId = useMemo(() => {
        if (!id) return '';
        return `fm-email-iframe-container__${id}`;
    }, [id]);

    useEffect(() => {
        const iframeContainer = document.getElementById(containerId);
        if (
            !cleanHtml?.length ||
            isIframeAttached.current ||
            !iframeContainer ||
            !isHtml ||
            !iframeContainerRef?.current?.offsetWidth
        )
            return;

        const head = cleanHtml.match(/<head[^>]*>[\s\S]*<\/head>/gi);
        const body = cleanHtml.match(/<body[^>]*>[\s\S]*<\/body>/gi);
        const iframe = document.createElement('iframe');
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframeContainer.appendChild(iframe);
        iframe.contentWindow.document.open();

        let fullHtml = '';
        if (head && body) fullHtml = `${head[0]}${body[0]}`;
        else {
            console.error('EmailIframe html has no head or body');
            return;
        }

        iframe.contentWindow.document.write(fullHtml);
        iframe.contentWindow.document.close();
        const iframeBody = iframe.contentWindow.document.body;
        iframeBody.style.margin = 0;
        iframeBody.style.padding = 0;
        iframeBody.style.width = iframeContainerRef.current.offsetWidth - 10;
        // For some reason adds a few pixels to the width that makes the scroll appear
        iframeBody.style.overflowX = 'clip';
        const bodyHeight = iframeBody.offsetHeight + 40;
        setIframeHeight(bodyHeight);
        isIframeAttached.current = true;

        const container = document.querySelector('.fm-activities__detail');
        if (!container) return;

        const updateIframeLayout = () => {
            if (!iframeContainerRef?.current) return;
            const bodyHeight = iframeBody.offsetHeight + 40;
            iframeBody.style.width = iframeContainerRef.current.offsetWidth - 10;
            setIframeHeight(bodyHeight);
        };

        let delayedUpdateId = null;
        const containerObserver = new ResizeObserver(() => {
            if (isHorizontalResizeActiveRef.current) {
                if (delayedUpdateId) {
                    window.clearTimeout(delayedUpdateId);
                }

                delayedUpdateId = window.setTimeout(() => {
                    if (!isHorizontalResizeActiveRef.current) {
                        updateIframeLayout();
                    }
                }, 120);
                return;
            }

            updateIframeLayout();
        });
        containerObserver.observe(container);

        return () => {
            containerObserver.disconnect();
            if (delayedUpdateId) {
                window.clearTimeout(delayedUpdateId);
            }
        };
    }, [isHtml, cleanHtml, containerId, id]);

    const iframeContainerStyles = useMemo(() => {
        if (!iframeHeight) return {};
        return { style: { height: `${iframeHeight}px` } };
    }, [iframeHeight]);

    if (!id) {
        console.error('EmailIframe needs an id as prop');
        return null;
    }

    return (
        <Fragment>
            {!isHtml && <TextWithLinks text={html} />}
            {isHtml && (
                <div
                    id={containerId}
                    className="fm-email-iframe-container"
                    ref={iframeContainerRef}
                    {...iframeContainerStyles}
                />
            )}
        </Fragment>
    );
});

export default EmailIframe;
